'use client';

import { useEffect, useState, useMemo, useCallback } from 'react';
import axios from 'axios';
import {
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Button,
    Box,
    Stack,
    TablePagination,
    Autocomplete,
    TextField,
    TableSortLabel,
    debounce,
    CircularProgress,
    Tooltip,
    IconButton,
    Switch,
    FormControlLabel,
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import SearchIcon from '@mui/icons-material/Search';
import { API_PATH } from '../../../App';
import { getAxiosConfig } from '../../../helpers/AuthHelper';
import Daterange from '../FinancialReport/Daterange';
import ResellerAmountModal from './ResellerAmountModal';
import TableLoader from '../GlobalComponents/TableLoader';
import ResellerBalanceModal from './ ResellerBalanceModal';
import { Balance } from '@mui/icons-material';

export type AccountsReport = {
    entityId: string | null;
    workerId: string | null;
    customerName: string | null;
    resellerName: string;
    spend: number;
    date: string;
    createdBy: string;
    type: string;
    currentBalance: number;
    workerGroup?: string;
};

export type WorkerGroupReport = {
    workerGroup: string;
    totalSpend: number;
    workerCount: number;
    customerName: string;
    resellerName: string;
    workers?: string[];
    uniqueWorkers?: number;
};

const transactionTypes = ['CREDIT', 'DEBIT'];

const AccountsControll = () => {
    const [activeButtons, setActiveButtons] = useState<Set<string>>(new Set(['resellers', 'spend', 'type', 'currentBalance']));
    const [selectedWorkers, setSelectedWorkers] = useState<string[]>([]);
    const [data, setData] = useState<AccountsReport[]>([]);
    const [groupedWorkerData, setGroupedWorkerData] = useState<WorkerGroupReport[]>([]);
    const [rawGroupedData, setRawGroupedData] = useState<AccountsReport[]>([]); // Store raw data for filtering
    const [groupByWorkerGroup, setGroupByWorkerGroup] = useState(false);
    const [isGroupedDataLoaded, setIsGroupedDataLoaded] = useState(false);
    const [searchWorkerGroup, setSearchWorkerGroup] = useState('');
    const [isGroupingLoading, setIsGroupingLoading] = useState(false);
    
    const today = new Date();
    const startOfToday = new Date(today.getFullYear(), today.getMonth(), today.getDate());
    const endOfToday = new Date(today.getFullYear(), today.getMonth(), today.getDate(), 23, 59, 59);

    const [fromDate, setFromDate] = useState<Date | null>(startOfToday);
    const [toDate, setToDate] = useState<Date | null>(endOfToday);
    const [filter, setFilter] = useState<string>('resellers');
    const [searchTrigger, setSearchTrigger] = useState<boolean>(false);

    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(200);
    const [totalRows, setTotalRows] = useState(0);
    const [loading, setLoading] = useState(false);
    const [resellerBalanceModalOpen, setResellerBalanceModalOpen] = useState(false);

    type Reseller = {
        id: number;
        resellerName: string;
    };

    type Customer = {
        id: number;
        customerGroupName: string;
    };

    const [resellers, setResellers] = useState<Reseller[]>([]);
    const [selectedResellers, setSelectedResellers] = useState<Reseller[]>([]);
    const [customers, setCustomers] = useState<Customer[]>([]);
    const [selectedCustomers, setSelectedCustomers] = useState<Customer[]>([]);
    const [allCustomers, setAllCustomers] = useState<any[]>([]);

    const [sortBy, setSortBy] = useState('date');
    const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');

    const [selectedType, setSelectedType] = useState('')

    const [workerSuggesionLoading, setWorkerSuggesionLoading] = useState(false);
    const [workerSuggesionOptions, setWorkerSuggesionOptions] = useState<string[]>([]);
    const [searchParam, setseachParam] = useState('');

    const [selectedworker, setSelectedWorker] = useState('')
    const [downloading, setDownloading] = useState(false);

    useEffect(() => {
        const fetchResellers = axios.get(
            API_PATH + "/customer_ledger/allResellers",
            getAxiosConfig()
        );
        const fetchCustomers = axios.get(
            API_PATH + "/customer_ledger/allCustomers",
            getAxiosConfig()
        );

        Promise.all([fetchResellers, fetchCustomers])
            .then(([resellerRes, customerRes]) => {
                setResellers(resellerRes.data);
                setAllCustomers(customerRes.data);
                setCustomers(
                    customerRes.data.map((customer: any) => ({
                        id: customer.id,
                        customerGroupName: customer.customerGroupName,
                    }))
                );
            })
            .catch((error) => {
                console.error("Error fetching reseller/customer data:", error);
            });
    }, []);

    useEffect(() => {
        if (selectedResellers.length > 0) {
            const selectedResellerIds = selectedResellers.map((r) => r.id);
            const filtered = allCustomers
                .filter((customer: any) =>
                    selectedResellerIds.includes(customer.reseller?.id)
                )
                .map((customer: any) => ({
                    id: customer.id,
                    customerGroupName: customer.customerGroupName,
                }));

            setCustomers(filtered);
        } else {
            const all = allCustomers.map((customer: any) => ({
                id: customer.id,
                customerGroupName: customer.customerGroupName,
            }));
            setCustomers(all);
        }
    }, [selectedResellers, allCustomers]);

    const formatLocalDate = (date: Date) => {
        const yyyy = date.getFullYear();
        const mm = String(date.getMonth() + 1).padStart(2, '0');
        const dd = String(date.getDate()).padStart(2, '0');
        return `${yyyy}-${mm}-${dd}`;
    };

    const extractWorkerGroup = (workerId: string | null): string => {
        if (!workerId) return '';
        const parts = workerId.split('.');
        return parts.length > 1 ? parts[0] : workerId;
    };

    const fetchRegularData = async () => {
        if (!fromDate || !toDate) return;
        
        setLoading(true);

        const formattedStartDate = formatLocalDate(fromDate);
        const formattedEndDate = formatLocalDate(toDate);
        const filters = Array.from(activeButtons);

        const params = new URLSearchParams();
        filters.forEach((filter) => params.append('filters', filter));
        params.append('startDate', formattedStartDate);
        params.append('endDate', formattedEndDate);
        params.append('page', page.toString());
        params.append('size', rowsPerPage.toString());
        selectedResellers.forEach((reseller) => {
            params.append('resellerIds', reseller.id.toString());
        });
        selectedCustomers.forEach((customers) => {
            params.append('customerIds', customers.id.toString());
        });
        sortBy && params.append('sortField', sortBy);
        params.append('workerIds', selectedworker)
        sortDirection && params.append('sortOrder', sortDirection);
        params.append('type', selectedType);
        activeButtons.has('date') ? params.append('groupByDate', 'true') : params.append('groupByDate', 'false');

        try {
            const response = await axios.get(`${API_PATH}/customer_ledger`, {
                params,
                ...getAxiosConfig(),
            });
            
            const allData = response.data.content;
            
            // Add workerGroup to each data item
            const dataWithWorkerGroups = allData.map((item: AccountsReport) => ({
                ...item,
                workerGroup: extractWorkerGroup(item.workerId)
            }));
            
            setData(dataWithWorkerGroups);
            setTotalRows(response.data.totalElements);
            setFilter(filters.join(','));
            
            // Store raw data for potential grouping
            if (groupByWorkerGroup) {
                setRawGroupedData(dataWithWorkerGroups);
            }
        } catch (error) {
            console.error('Error fetching data:', error);
        } finally {
            setLoading(false);
        }
    };


    const fetchAllDataForWorkerGrouping = async (forceRefresh = false) => {
        if (!fromDate || !toDate) return;
        
        // Add date-based check - if dates have changed, we need to force refresh
        const dateKey = `${formatLocalDate(fromDate)}_${formatLocalDate(toDate)}`;
        const currentDateKey = `${fromDate?.toISOString()}_${toDate?.toISOString()}`;
        
        // Create a date-based cache key to check if we need fresh data
        const storedDateKey = localStorage.getItem('groupedDataDateKey');
        
        // If dates have changed or force refresh is true, fetch new data
        const shouldFetchNewData = forceRefresh || !storedDateKey || storedDateKey !== dateKey;
        
        if (!shouldFetchNewData && isGroupedDataLoaded) {
            // Just process the existing data we have
            processGroupedWorkerDataFromStored();
            return;
        }
        
        setIsGroupingLoading(true);

        const formattedStartDate = formatLocalDate(fromDate);
        const formattedEndDate = formatLocalDate(toDate);
        const filters = Array.from(activeButtons);

        const params = new URLSearchParams();
        filters.forEach((filter) => params.append('filters', filter));
        params.append('startDate', formattedStartDate);
        params.append('endDate', formattedEndDate);
        params.append('page', '0');
        params.append('size', '1'); // Just to get the total count
        params.append('workerIds', selectedworker);
        params.append('type', selectedType);
        activeButtons.has('date') ? params.append('groupByDate', 'true') : params.append('groupByDate', 'false');

        try {
            // First API call to get total count
            const countResponse = await axios.get(`${API_PATH}/customer_ledger`, {
                params,
                ...getAxiosConfig(),
            });
            
            const totalElements = countResponse.data.totalElements;
            
            if (totalElements === 0) {
                setGroupedWorkerData([]);
                setRawGroupedData([]);
                setIsGroupingLoading(false);
                setIsGroupedDataLoaded(true);
                localStorage.setItem('groupedDataDateKey', dateKey);
                return;
            }
            
            // Second API call to get ALL data using totalElements as size
            params.set('size', totalElements.toString());
            
            const response = await axios.get(`${API_PATH}/customer_ledger`, {
                params,
                ...getAxiosConfig(),
            });
            
            const allData = response.data.content.map((item: AccountsReport) => ({
                ...item,
                workerGroup: extractWorkerGroup(item.workerId)
            }));
            
            // Store raw data
            setRawGroupedData(allData);
            
            // Process and display grouped data
            processGroupedWorkerDataFromStored(allData);
            setIsGroupedDataLoaded(true);
            
            // Store the date key for future reference
            localStorage.setItem('groupedDataDateKey', dateKey);
            
        } catch (error) {
            console.error('Error fetching all data for grouping:', error);
        } finally {
            setIsGroupingLoading(false);
        }
    };

    // Also update the main useEffect to reset grouped data when dates change
    useEffect(() => {
        if (groupByWorkerGroup && activeButtons.has('workers') && !activeButtons.has('customers') && !activeButtons.has('resellers')) {
            // When dates change, we need to fetch new data
            const shouldResetGroupedData = true;
            
            if (shouldResetGroupedData) {
                // Reset the grouped data flag and clear data
                setIsGroupedDataLoaded(false);
                setGroupedWorkerData([]);
                setRawGroupedData([]);
                localStorage.removeItem('groupedDataDateKey');
            }
        }
    
    fetchData();
        }, [
            fromDate,  // This should trigger a refresh
            toDate,    // This should trigger a refresh
            searchTrigger,
            page,
            rowsPerPage,
            selectedResellers,
            selectedCustomers,
            selectedWorkers,
            selectedType,
            sortBy,
            sortDirection,
            selectedworker,
            groupByWorkerGroup,
            // Note: searchWorkerGroup is handled separately above
        ]);

    // Process grouped data from stored raw data
    const processGroupedWorkerDataFromStored = useCallback((rawDataParam?: AccountsReport[]) => {
        const rawData = rawDataParam || rawGroupedData;
        
        if (!rawData || rawData.length === 0) {
            setGroupedWorkerData([]);
            setTotalRows(0);
            return;
        }
        
        const groups: Record<string, WorkerGroupReport> = {};
        
        rawData.forEach((item: AccountsReport) => {
            const workerGroup = extractWorkerGroup(item.workerId);
            
            if (!workerGroup) return;
            
            // Create a unique key based on workerGroup + customerName + resellerName
            const groupKey = `${workerGroup}_${item.customerName || 'NO_CUSTOMER'}_${item.resellerName || 'NO_RESELLER'}`;
            
            if (!groups[groupKey]) {
                groups[groupKey] = {
                    workerGroup: workerGroup,
                    totalSpend: 0,
                    workerCount: 0,
                    customerName: item.customerName || 'N/A',
                    resellerName: item.resellerName || 'N/A',
                    workers: [],
                    uniqueWorkers: 0
                };
            }
            
            groups[groupKey].totalSpend += item.spend;
            
            // Track unique workers
            if (item.workerId && !groups[groupKey].workers?.includes(item.workerId)) {
                groups[groupKey].workers?.push(item.workerId);
                groups[groupKey].workerCount += 1;
                groups[groupKey].uniqueWorkers = groups[groupKey].workers?.length || 0;
            }
        });
        
        let groupedData = Object.values(groups);
        
        // Apply search filter if exists
        if (searchWorkerGroup) {
            groupedData = groupedData.filter(group => 
                group.workerGroup.toLowerCase().includes(searchWorkerGroup.toLowerCase()) ||
                group.customerName.toLowerCase().includes(searchWorkerGroup.toLowerCase()) ||
                group.resellerName.toLowerCase().includes(searchWorkerGroup.toLowerCase())
            );
        }
        
        // Sort by total spend descending by default
        groupedData.sort((a, b) => b.totalSpend - a.totalSpend);
        
        setGroupedWorkerData(groupedData);
        setTotalRows(groupedData.length);
    }, [rawGroupedData, searchWorkerGroup]);

    // Main fetch function that decides which data to fetch
    const fetchData = async () => {
        if (!fromDate || !toDate) return;
        
        if (!activeButtons.has('workers')) {
            setSelectedWorker('')
        }
        
        if (groupByWorkerGroup && activeButtons.has('workers') && !activeButtons.has('customers') && !activeButtons.has('resellers')) {
            // Fetch grouped worker data
            await fetchAllDataForWorkerGrouping();
        } else {
            // Reset flag when not in group mode
            setIsGroupedDataLoaded(false);
            // Fetch regular data
            await fetchRegularData();
            setGroupedWorkerData([]);
        }
    };

    // Handle group by worker group toggle
    const handleGroupByWorkerGroupToggle = () => {
        const newGroupByState = !groupByWorkerGroup;
        setGroupByWorkerGroup(newGroupByState);
        
        if (newGroupByState) {
            // When enabling grouping, clear other filters that conflict
            setSelectedCustomers([]);
            setSelectedResellers([]);
            setSelectedType('');
            setPage(0);
            setSearchWorkerGroup('');
            // Reset the loaded flag to ensure fresh data
            setIsGroupedDataLoaded(false);
        } else {
            // When disabling grouping, clear search and reset flag
            setSearchWorkerGroup('');
            setIsGroupedDataLoaded(false);
        }
    };

    // Handle search worker group changes
    const handleSearchWorkerGroupChange = (value: string) => {
        setSearchWorkerGroup(value);
        
        // If we're in group mode and have data, just filter it locally
        if (groupByWorkerGroup && isGroupedDataLoaded && rawGroupedData.length > 0) {
            processGroupedWorkerDataFromStored();
        }
    };

    // Effect for handling search worker group with debounce
    useEffect(() => {
        if (!groupByWorkerGroup) return;
        
        // Only make API call if we don't have data loaded yet and user is searching
        if (!isGroupedDataLoaded && searchWorkerGroup) {
            const timer = setTimeout(() => {
                fetchAllDataForWorkerGrouping();
            }, 500); // Debounce for 500ms
            
            return () => clearTimeout(timer);
        }
        
        // If we have data and user clears search, show all data
        if (isGroupedDataLoaded && searchWorkerGroup === '' && rawGroupedData.length > 0) {
            processGroupedWorkerDataFromStored();
        }
    }, [searchWorkerGroup, groupByWorkerGroup, isGroupedDataLoaded]);

    // Main data fetching effect
    useEffect(() => {
        fetchData();
    }, [
        fromDate,
        toDate,
        searchTrigger,
        page,
        rowsPerPage,
        selectedResellers,
        selectedCustomers,
        selectedWorkers,
        selectedType,
        sortBy,
        sortDirection,
        selectedworker,
        groupByWorkerGroup,
        // Note: searchWorkerGroup is handled separately above
    ]);

    const downloadRegularData = () => {
        setDownloading(true);
        
        const visibleColumns = columns.filter(col => {
            if (col.visibleKey === 'currentBalance') {
                return activeButtons.has('currentBalance') && 
                       !(activeButtons.has('customers') || activeButtons.has('workers'));
            }
            return col.visibleKey ? activeButtons.has(col.visibleKey) : true;
        });

        const headers = visibleColumns.map(col => col.label);
        
        const csvRows = data.map(row => {
            const rowData = [];
            
            if (activeButtons.has('workers')) rowData.push(row.workerId || '');
            if (activeButtons.has('customers')) rowData.push(row.customerName || '');
            if (activeButtons.has('resellers')) rowData.push(row.resellerName || '');
            if (activeButtons.has('spend')) rowData.push(row.spend.toFixed(10));
            
            if (activeButtons.has('currentBalance') && 
                !(activeButtons.has('customers') || activeButtons.has('workers'))) {
                rowData.push(row.currentBalance.toFixed(10));
            }
            
            if (activeButtons.has('date')) rowData.push(new Date(row.date).toLocaleDateString('en-GB'));
            if (activeButtons.has('created by')) rowData.push(row.createdBy || '');
            if (activeButtons.has('type')) rowData.push(row.type || '');

            return rowData;
        });

        const csvContent = [
            headers.join(','),
            ...csvRows.map(row => row.map(cell => `"${cell}"`).join(','))
        ].join('\n');

        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        
        const dateStr = new Date().toISOString().split('T')[0];
        const filtersStr = Array.from(activeButtons).join('_');
        const filename = `accounts_report_${dateStr}_${filtersStr}.csv`;
        
        link.setAttribute('href', url);
        link.setAttribute('download', filename);
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        
        setDownloading(false);
    };

    const downloadGroupedWorkerData = () => {
        setDownloading(true);
        
        // Create CSV header for grouped data
        const headers = ['Worker Group', 'Total Spend', 'Worker Count', 'Customer', 'Reseller', 'Workers'];
        
        // Create CSV rows
        const csvRows = groupedWorkerData.map(row => [
            row.workerGroup,
            row.totalSpend.toFixed(10),
            row.workerCount.toString(),
            row.customerName || '',
            row.resellerName || '',
            row.workers?.join('; ') || ''
        ]);

        // Combine headers and rows
        const csvContent = [
            headers.join(','),
            ...csvRows.map(row => row.map(cell => `"${cell}"`).join(','))
        ].join('\n');

        // Create download link
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        
        const dateStr = new Date().toISOString().split('T')[0];
        const filename = `worker_groups_report_${dateStr}.csv`;
        
        link.setAttribute('href', url);
        link.setAttribute('download', filename);
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        
        setDownloading(false);
    };

    const downloadAllData = async () => {
        if (!fromDate || !toDate) return;
        
        if (groupByWorkerGroup) {
            downloadGroupedWorkerData();
            return;
        }
        
        setDownloading(true);

        const formattedStartDate = formatLocalDate(fromDate);
        const formattedEndDate = formatLocalDate(toDate);
        const filters = Array.from(activeButtons);

        const params = new URLSearchParams();
        filters.forEach((filter) => params.append('filters', filter));
        params.append('startDate', formattedStartDate);
        params.append('endDate', formattedEndDate);
        params.append('size', totalRows.toString());
        params.append('page', '0');
        selectedResellers.forEach((reseller) => {
            params.append('resellerIds', reseller.id.toString());
        });
        selectedCustomers.forEach((customers) => {
            params.append('customerIds', customers.id.toString());
        });
        sortBy && params.append('sortField', sortBy);
        params.append('workerIds', selectedworker);
        sortDirection && params.append('sortOrder', sortDirection);
        params.append('type', selectedType);
        activeButtons.has('date') ? params.append('groupByDate', 'true') : params.append('groupByDate', 'false');

        try {
            const response = await axios.get(`${API_PATH}/customer_ledger`, {
                params,
                ...getAxiosConfig(),
            });
            
            const allData = response.data.content;
            
            const visibleColumns = columns.filter(col => {
                if (col.visibleKey === 'currentBalance') {
                    return activeButtons.has('currentBalance') && 
                           !(activeButtons.has('customers') || activeButtons.has('workers'));
                }
                return col.visibleKey ? activeButtons.has(col.visibleKey) : true;
            });

            const headers = visibleColumns.map(col => col.label);
            
            const csvRows = allData.map((row: AccountsReport) => {
                const rowData: string[] = [];
                
                if (activeButtons.has('workers')) rowData.push(row.workerId || '');
                if (activeButtons.has('customers')) rowData.push(row.customerName || '');
                if (activeButtons.has('resellers')) rowData.push(row.resellerName || '');
                if (activeButtons.has('spend')) rowData.push(row.spend.toFixed(10));
                
                if (activeButtons.has('currentBalance') && 
                    !(activeButtons.has('customers') || activeButtons.has('workers'))) {
                    rowData.push(row.currentBalance.toFixed(10));
                }
                
                if (activeButtons.has('date')) rowData.push(new Date(row.date).toLocaleDateString('en-GB'));
                if (activeButtons.has('created by')) rowData.push(row.createdBy || '');
                if (activeButtons.has('type')) rowData.push(row.type || '');

                return rowData;
            });

            const csvContent = [
                headers.join(','),
                ...csvRows.map((row: string[]) => row.map((cell: string) => `"${cell}"`).join(','))
            ].join('\n');

            const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
            const url = URL.createObjectURL(blob);
            const link = document.createElement('a');
            
            const dateStr = new Date().toISOString().split('T')[0];
            const filtersStr = Array.from(activeButtons).join('_');
            const filename = `accounts_report_all_${dateStr}_${filtersStr}.csv`;
            
            link.setAttribute('href', url);
            link.setAttribute('download', filename);
            link.style.visibility = 'hidden';
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            
        } catch (error) {
            console.error('Error downloading all data:', error);
        } finally {
            setDownloading(false);
        }
    };

    const handleChangePage = (_: unknown, newPage: number) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const handleButtonClick = (type: string) => {
        setActiveButtons((prev) => {
            const updated = new Set(prev);
            const isRemoving = updated.has(type);
            const isWorkerActive = updated.has('workers');
            const isCustomerActive = updated.has('customers');

            if (type === "currentBalance" && (isWorkerActive || isCustomerActive)) {
                return prev;
            }

            // When workers is clicked, disable group by worker group if turning off
            if (type === 'workers' && isRemoving) {
                setGroupByWorkerGroup(false);
                setIsGroupedDataLoaded(false);
            }

            if (isRemoving) {
                updated.delete(type);

                const stillHasOneOfThree =
                    updated.has('resellers') || updated.has('customers') || updated.has('workers');

                if (
                    type === 'workers' &&
                    prev.has('workers') &&
                    prev.has('resellers') &&
                    prev.has('customers')
                ) {
                    updated.delete('customers');
                    setSelectedCustomers([]);
                }

                if (!stillHasOneOfThree) {
                    return prev;
                }

                if (type === 'resellers') setSelectedResellers([]);
                if (type === 'customers') setSelectedCustomers([]);
                if (type === 'workers') {
                    setSelectedWorkers([]);
                    setGroupByWorkerGroup(false);
                    setIsGroupedDataLoaded(false);
                }
            } else {
                if ((type === 'workers' || type === 'customers') && updated.has('currentBalance')) {
                    updated.delete('currentBalance');
                }

                const willBeWorkerActive = type === 'workers' || isWorkerActive;

                if (type === 'resellers' && updated.has('customers') && !willBeWorkerActive) {
                    updated.delete('customers');
                    setSelectedCustomers([]);
                }

                if (type === 'customers' && updated.has('resellers') && !willBeWorkerActive) {
                    updated.delete('resellers');
                    setSelectedResellers([]);
                }

                updated.add(type);

                if (type === 'resellers' && selectedResellers.length === 0) setSelectedResellers([]);
                if (type === 'customers' && selectedCustomers.length === 0) setSelectedCustomers([]);
                if (type === 'workers' && selectedWorkers.length === 0) setSelectedWorkers([]);
            }

            setSearchTrigger(prev => !prev);
            return updated;
        });
    };

    const columns = [
        { id: 'workerId', label: 'Worker ID', visibleKey: 'workers' },
        { id: 'customerName', label: 'Customer', visibleKey: 'customers' },
        { id: 'resellerName', label: 'Reseller Name', visibleKey: 'resellers' },
        { id: 'spend', label: 'Spend', visibleKey: 'spend' },
        { id: 'currentBalance', label: 'Current Balance', visibleKey: 'currentBalance' },
        { id: 'date', label: 'Date', visibleKey: 'date' },
        { id: 'createdBy', label: 'Created By', visibleKey: 'created by' },
        { id: 'type', label: 'Type', visibleKey: 'type' },
    ];

    const groupedWorkerColumns = [
        { id: 'workerGroup', label: 'Worker Group' },
        { id: 'customerName', label: 'Customer' },
        { id: 'resellerName', label: 'Reseller' },
        { id: 'totalSpend', label: 'Total Spend' },
        { id: 'uniqueWorkers', label: 'Unique Workers' },
        // { id: 'workerCount', label: 'Transaction Count' }, // Renamed for clarity
    ];

    const handleSort = (field: string) => {
        if (sortBy === field) {
            setSortDirection((prev) => (prev === 'asc' ? 'desc' : 'asc'));
        } else {
            setSortBy(field);
            setSortDirection('asc');
        }
    };

    const fetchSuggestions = debounce(async (query: string) => {
        if (!query) return;

        setWorkerSuggesionLoading(true);
        try {
            const res = await axios.get(`${API_PATH}/customer_ledger/search?query=${query}`, getAxiosConfig());
            setWorkerSuggesionOptions(res.data);
            if (res.data.length === 1) {
                setSelectedWorker(res.data[0]);
            } else setSelectedWorker('')
        } catch (err) {
            console.error('Failed to fetch suggestions:', err);
        } finally {
            setWorkerSuggesionLoading(false);
        }
    }, 300);

    useEffect(() => {
        fetchSuggestions(searchParam);
    }, [searchParam]);

    return (
        <Box>
            <Stack spacing={2} direction="row" sx={{ m: 2, flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between' }}>

                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                    <Daterange
                        fromDate={fromDate}
                        toDate={toDate}
                        setFromDate={setFromDate}
                        setToDate={setToDate}
                        setSearchTrigger={setSearchTrigger}
                        searchTrigger={searchTrigger}
                    />

                    {!groupByWorkerGroup && activeButtons.has('resellers') && (
                        <Autocomplete
                            multiple
                            options={resellers}
                            getOptionLabel={(option) => option.resellerName}
                            isOptionEqualToValue={(option, value) => option.id === value.id}
                            value={selectedResellers}
                            onChange={(_, val) => setSelectedResellers(val)}
                            renderInput={(params) => <TextField {...params} label="Resellers" size="small" />}
                            sx={{ minWidth: 200 }}
                        />
                    )}

                    {!groupByWorkerGroup && activeButtons.has('customers') && (
                        <Autocomplete
                            multiple
                            options={customers}
                            getOptionLabel={(option) => option.customerGroupName}
                            isOptionEqualToValue={(option, value) => option.id === value.id}
                            value={selectedCustomers}
                            onChange={(_, val) => setSelectedCustomers(val)}
                            renderInput={(params) => <TextField {...params} label="Customers" size="small" />}
                            sx={{ minWidth: 200 }}
                        />
                    )}

                    {activeButtons.has('workers') && !groupByWorkerGroup && (
                        <Autocomplete
                            freeSolo
                            options={workerSuggesionOptions}
                            value={selectedworker}
                            onChange={(_, val) => setSelectedWorker(val || '')}
                            inputValue={searchParam}
                            onInputChange={(_, val) => setseachParam(val)}
                            loading={workerSuggesionLoading}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    label="Search worker"
                                    size="small"
                                    InputProps={{
                                        ...params.InputProps,
                                        endAdornment: (
                                            <>
                                                {workerSuggesionLoading ? <CircularProgress color="inherit" size={16} /> : null}
                                                {params.InputProps.endAdornment}
                                            </>
                                        ),
                                    }}
                                />
                            )}
                            sx={{ minWidth: 200 }}
                        />
                    )}

                    {groupByWorkerGroup && (
                        <TextField
                            label="Search Worker Group"
                            value={searchWorkerGroup}
                            onChange={(e) => handleSearchWorkerGroupChange(e.target.value)}
                            size="small"
                            sx={{ minWidth: 200 }}
                            InputProps={{
                                endAdornment: (
                                    <SearchIcon color="action" sx={{ mr: 1 }} />
                                ),
                            }}
                        />
                    )}

                    {!groupByWorkerGroup && !activeButtons.has('customers') && !activeButtons.has('workers') && activeButtons.has('resellers') && (
                        <Autocomplete
                            options={transactionTypes}
                            value={selectedType}
                            onChange={(_, val) => setSelectedType(val || '')}
                            renderInput={(params) => <TextField {...params} label="Type" size="small" />}
                            sx={{ minWidth: 200 }}
                        />
                    )}

                    {activeButtons.has('workers') && !activeButtons.has('customers') && !activeButtons.has('resellers') && (
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={groupByWorkerGroup}
                                    onChange={handleGroupByWorkerGroupToggle}
                                    color="primary"
                                    disabled={isGroupingLoading}
                                />
                            }
                            label="Group by Worker Group"
                            sx={{ ml: 1 }}
                        />
                    )}

                </Box>

                <Box sx={{ ml: 'auto', display: 'flex', gap: 1 }}>            
                    <Tooltip title="Download all data (with current filters)">
                        <Button
                            variant="outlined"
                            startIcon={<DownloadIcon />}
                            onClick={downloadAllData}
                            disabled={totalRows === 0 || downloading}
                            size="small"
                        >
                            {downloading ? 'Downloading...' : 'Download All'}
                        </Button>
                    </Tooltip>
                                    <Button
                    variant="outlined"
                    startIcon={<Balance/>}
                    onClick={() => setResellerBalanceModalOpen(true)}
                    size="small"
                    sx={{ 
                        textTransform: 'none',
                        borderRadius: 2,
                        px: 2,
                        height: 36,
                    }}
                >
                    All Reseller Accounts
                </Button>
                    
                    <ResellerAmountModal />
                </Box>

            </Stack>

            <Box display="flex" width="100%">

                {loading || (groupByWorkerGroup && isGroupingLoading) ? (
                    <TableContainer component={Paper} sx={{ width: '85%', margin: 'left' }}>
                        <TableLoader />
                    </TableContainer>
                ) : groupByWorkerGroup ? (
                    // Render grouped worker data table
                    <TableContainer component={Paper} sx={{ width: '85%', margin: 'left' }}>
                        <Table>
                            <TableHead>
                                <TableRow>
                                    {groupedWorkerColumns.map(({ id, label }) => (
                                        <TableCell key={id}>
                                            {label}
                                        </TableCell>
                                    ))}
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {groupedWorkerData && groupedWorkerData.map((row, index) => (
                                    <TableRow key={index} hover>
                                        <TableCell>
                                            <Tooltip title={`Workers: ${row.workers?.join(', ') || 'None'}`}>
                                                <span style={{ cursor: 'pointer', fontWeight: 'bold' }}>
                                                    {row.workerGroup}
                                                </span>
                                            </Tooltip>
                                        </TableCell>
                                        <TableCell>
                                            {row.customerName}
                                        </TableCell>
                                        <TableCell>
                                            {row.resellerName}
                                        </TableCell>
                                        <TableCell sx={{ color: 'green', fontWeight: 'bold' }}>
                                            {row.totalSpend.toFixed(10)}
                                        </TableCell>
                                        <TableCell>
                                            {row.uniqueWorkers || 0}
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                ) : (
                    // Render regular data table
                    <TableContainer component={Paper} sx={{ width: '85%', margin: 'left' }}>
                        <Table>
                            <TableHead>
                                <TableRow>
                                    {columns.map(({ id, label, visibleKey }) => {
                                        if (visibleKey === 'currentBalance') {
                                            if (!activeButtons.has('currentBalance')) return null;
                                            if (activeButtons.has('customers') || activeButtons.has('workers')) return null;
                                        } else if (visibleKey && !activeButtons.has(visibleKey)) {
                                            return null;
                                        }

                                        return (
                                            <TableCell key={id} sortDirection={sortBy === id ? sortDirection : false}>
                                                <TableSortLabel
                                                    active={sortBy === id}
                                                    direction={sortBy === id ? sortDirection : 'asc'}
                                                    onClick={() => handleSort(id)}
                                                >
                                                    {label}
                                                </TableSortLabel>
                                            </TableCell>
                                        );
                                    })}
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {data && data.map((row, index) => (
                                    <TableRow key={index}>
                                        {activeButtons.has('workers') && <TableCell>{row.workerId}</TableCell>}
                                        {activeButtons.has('customers') && <TableCell>{row.customerName}</TableCell>}
                                        {activeButtons.has('resellers') && <TableCell>{row.resellerName}</TableCell>}
                                        {activeButtons.has('spend') && <TableCell sx={{ color: row.type === 'CREDIT' ? 'green' : 'red' }}>
                                            {row.spend.toFixed(10)}
                                        </TableCell>}
                                        {activeButtons.has('currentBalance') &&
                                            activeButtons.has('currentBalance') &&
                                            !(activeButtons.has('customers') || activeButtons.has('workers')) && (
                                                <TableCell>{row.currentBalance.toFixed(10)}</TableCell>
                                            )}
                                        {activeButtons.has('date') && <TableCell>
                                            {new Date(row.date).toLocaleDateString('en-GB')}
                                        </TableCell>}
                                        {activeButtons.has('created by') && <TableCell>{row.createdBy}</TableCell>}
                                        {activeButtons.has('type') && <TableCell>{row.type}</TableCell>}

                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                )}
                <Box
                    sx={{
                        width: '15%',
                        backgroundColor: '#ffe6e608',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        padding: 2,
                        gap: 1,
                        boxShadow: 2,
                        borderLeft: '1px solid #ccc',
                    }}
                >
                    {columns.map((col) => {
                        let disabled = false;
                        let tooltip = '';

                        if (col.visibleKey === 'currentBalance' && (activeButtons.has('customers') || activeButtons.has('workers'))) {
                            disabled = true;
                            tooltip = 'Cannot show Current Balance while Customers or Workers filter is active';
                        }

                        if (col.visibleKey === 'customers' && activeButtons.has('resellers') && selectedResellers.length === 0) {
                            disabled = true;
                            tooltip = 'Select at least one Reseller to enable Customers';
                        }

                        const button = (
                            <Button
                                key={col.visibleKey}
                                fullWidth
                                color={activeButtons.has(col.visibleKey) ? 'info' : 'secondary'}
                                variant={activeButtons.has(col.visibleKey) ? 'contained' : 'outlined'}
                                onClick={() => handleButtonClick(col.visibleKey)}
                                size="medium"
                                sx={{
                                    padding: '6px 12px',
                                    fontSize: '0.8rem',
                                    borderRadius: '8px',
                                    textTransform: 'uppercase',
                                }}
                                disabled={disabled}
                            >
                                {col.visibleKey}
                            </Button>
                        );

                        return tooltip ? <Tooltip key={col.visibleKey} title={tooltip}>{button}</Tooltip> : button;
                    })}
                </Box>

            </Box>

            {!groupByWorkerGroup && (
                <TablePagination
                    component="div"
                    count={totalRows}
                    page={page}
                    onPageChange={handleChangePage}
                    rowsPerPage={rowsPerPage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                    rowsPerPageOptions={[100, 200, 500, 1000]}
                />
            )}
            <ResellerBalanceModal 
                open={resellerBalanceModalOpen}
                onClose={() => setResellerBalanceModalOpen(false)}
            />
        </Box>
    );
};

export default AccountsControll;
